import isEmpty from 'lodash/isEmpty'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'

import documents from 'modules/documents/store/collections/documents'
import type Employee from 'modules/employees/store/models/Employee'
import type { CustomFields } from 'modules/timeoff/lib/DocumentForm'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import type Leave from 'modules/timeoff/store/models/Leave'

interface Props {
  year: number
  leave: Leave
  employee: Employee | null | undefined
  form: LeaveForm
  documentForm: DocumentForm
  canUpdateLeave: boolean
  fetchStats: (leave: Leave) => void
  onChange?: () => void
  getCustomFieldsValues: () => Promise<CustomFields>
  closePortal: ClosePortalFn
}

const useUpdateLeave = ({
  year,
  leave,
  employee,
  form,
  documentForm,
  canUpdateLeave,
  fetchStats,
  onChange,
  getCustomFieldsValues,
  closePortal,
}: Props) => {
  const { fetchData } = useLeaves()

  const saveDocument = async (leave: Leave) => {
    const customFieldsValues = await getCustomFieldsValues()

    if (isEmpty(documentForm.get('file').value)) return

    documentForm.setDocumentValues(leave, customFieldsValues)
    await documentForm.create(documents)
  }

  const updateLeave = async () => (canUpdateLeave ? await form.save(leave) : true)

  const { performAction: onSubmit, running: updateLoading } = useAsyncAction(async () => {
    const canUpdate = await updateLeave()

    if (canUpdate) {
      await saveDocument(leave)
      fetchStats(leave)
      fetchData && fetchData(employee)
      closePortal()

      if (onChange) onChange()
    }
  })

  return {
    onSubmit,
    updateLoading,
  }
}

export default useUpdateLeave
